import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NotificationManager } from 'react-notifications';
import BtChecklistSelect from '../bt-checklist-select/bt-checklist-select';
import appState from '../../state/AppStateContainer';
import { ServiceErfa } from '../../services/service-erfa';
import { EvaluationLimits } from '../../util/evaluationContract';
import { reportUnexpected } from '../page-supplier-evaluations/reportUnexpected';
import { hasGlobalNotice } from './evaluationStart';
import styles from './app-evaluation-start.module.scss';

type QuestionOption = { value: string; label: string };

export type QuestionPickerProps = {
  value: string[];
  // picked: the { value, label } of every id, in the same order
  onChange: (ids: string[], picked: QuestionOption[]) => void;
  max?: number;
  mode?: 'own' | 'extra';
  // Texts the parent already knows, used for picks the loaded options lack
  labels?: Record<string, string>;
};

type ErfaQuestion = { _id: string; question: string };

// One slot stays free for the Övrigt question every utvärdering ends with.
const MAX_QUESTIONS = EvaluationLimits.MaxQuestionsPerRequest - 1;

const toOption = (question: ErfaQuestion): QuestionOption => ({
  value: question._id,
  label: question.question,
});

const QuestionPicker = (props: QuestionPickerProps) => {
  const { value, max = MAX_QUESTIONS, mode = 'own' } = props;
  const [t] = useTranslation('evaluations');
  const [options, setOptions] = useState<QuestionOption[]>([]);
  const optionsRef = useRef(options);
  const [loaded, setLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const latest = useRef(props);
  latest.current = props;
  // Bumped when the user clears the picks, so a create that resolves afterwards adds nothing.
  const picksCleared = useRef(0);
  const mounted = useRef(true);

  const updateOptions = (next: (current: QuestionOption[]) => QuestionOption[]) => {
    optionsRef.current = next(optionsRef.current);
    setOptions(optionsRef.current);
  };

  const labelOf = (id: string) =>
    optionsRef.current.find(option => option.value === id)?.label ?? latest.current.labels?.[id];

  const emit = (ids: string[]) =>
    latest.current.onChange(
      ids,
      ids.map(id => ({ value: id, label: labelOf(id) ?? id })),
    );

  useEffect(() => {
    mounted.current = true;
    ServiceErfa.getQuestions()
      .then((questions: ErfaQuestion[]) => {
        if (!mounted.current) return;
        const fetched = questions.map(toOption);
        optionsRef.current = [
          ...fetched,
          ...optionsRef.current.filter(
            option => !fetched.some(item => item.value === option.value),
          ),
        ];
        setOptions(optionsRef.current);
        setLoaded(true);
      })
      .catch((error: unknown) => {
        reportUnexpected(error);
        if (mounted.current) setLoadFailed(true);
      });
    return () => {
      mounted.current = false;
    };
  }, []);

  const isExtra = mode === 'extra';
  const unloaded = value
    .filter(id => !options.some(option => option.value === id) && props.labels?.[id])
    .map(id => ({ value: id, label: props.labels?.[id] as string }));
  const atCap = value.length >= max;
  const capText = isExtra
    ? t('quick.questions.extraCap', {
        count: Math.max(0, max),
        defaultValue_one: 'Högst {{count}} tillagd fråga (40 frågor per utvärdering).',
        defaultValue_other: 'Högst {{count}} tillagda frågor (40 frågor per utvärdering).',
      })
    : t(
        'quick.questions.capReached',
        'Högst {{max}} frågor per utvärdering. Ta bort en fråga för att välja en annan.',
        { max },
      );

  const handleChange = (next: string[]) => {
    if (!next.length) picksCleared.current += 1;
    const kept = next.filter(id => value.includes(id));
    const added = next.filter(id => !value.includes(id));
    const room = Math.max(0, max - kept.length);
    if (added.length > room) NotificationManager.info(capText);
    emit([...kept, ...added.slice(0, room)]);
  };

  const handleCreate = async (text: string) => {
    const clearedAtStart = picksCleared.current;
    try {
      const created: ErfaQuestion = await ServiceErfa.addQuestion({ question: text });
      if (!mounted.current) return;
      updateOptions(current =>
        current.some(option => option.value === created._id)
          ? current
          : [...current, toOption(created)],
      );
      const current = latest.current;
      if (picksCleared.current !== clearedAtStart) return;
      if (current.value.includes(created._id)) return;
      if (current.value.length >= (current.max ?? MAX_QUESTIONS)) {
        NotificationManager.info(capText);
        return;
      }
      emit([...current.value, created._id]);
    } catch (error) {
      reportUnexpected(error);
      if (!mounted.current || hasGlobalNotice(error)) return;
      NotificationManager.error(
        t('quick.questions.createFailed', 'Det gick inte att skapa frågan'),
      );
    }
  };

  return (
    <div className={styles.fieldStack}>
      <BtChecklistSelect
        label={isExtra ? undefined : t('quick.questions.label', 'Frågor')}
        placeholder={
          loaded || loadFailed
            ? t('quick.questions.placeholder', 'Välj frågor')
            : t('quick.questions.loading', 'Hämtar frågor…')
        }
        options={unloaded.length ? [...options, ...unloaded] : options}
        value={value}
        onChange={handleChange}
        onCreate={appState.isWriteAccessRole() && !atCap ? handleCreate : undefined}
        searchPlaceholder={
          !atCap
            ? undefined
            : isExtra
            ? capText
            : t(
                'quick.questions.capSearch',
                'Högst {{max}} frågor. Ta bort en för att välja en annan.',
                {
                  max,
                },
              )
        }
      />
      {loadFailed && (
        <p className={styles.hint}>
          {t('quick.questions.loadFailed', 'Frågorna kunde inte hämtas. Försök igen senare.')}
        </p>
      )}
      {atCap && <p className={styles.hint}>{capText}</p>}
      {!isExtra && (
        <p className={styles.hint}>
          {t(
            'quick.questions.answerHint',
            'Varje fråga besvaras med 1–5 stjärnor eller Vet ej. Övrigt läggs alltid till sist som en fritextfråga.',
          )}
        </p>
      )}
    </div>
  );
};

export default QuestionPicker;
